import { Injectable } from "@angular/core";
import { Observable, catchError, retry } from "rxjs";
import { BaseService } from "./base.service";
import { HttpClient } from "@angular/common/http";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { Countries } from "../models/countries";
import { AddFarm } from "../models/addFarm";
import { FarmId } from "../models/farmId";
import { RemoveArea } from "../models/removeArea";
import { FieldArea } from "../models/fieldArea";
import { ContaningField } from "../models/containingField";
import { MultiPolygonField } from "../models/multiPolygonField";
import { FieldName } from "../models/fieldName";
import { FieldId } from "../models/fieldId";
import { CirclePolygon } from "../models/circlePolygon";
import { Polygon } from "../models/polygon";
import { AddField } from "../models/addField";
import { UpdateFarmDto } from "../dto/UpdateFarmDto";

@Injectable({
    providedIn: 'root'
  })
  export class BoundaryManagementService extends BaseService {

    private API_BOUNDARY_MANAGEMENT_URL = this.API_BASE_URL + "boundary-management"; 

    constructor(private httpClient: HttpClient,
        public _snackBar: MatSnackBar,
        public cookieService: CookieService,
        public translate: TranslateService) {
            super()
      }

    ////////////////////////////////////////////////////////////////////////////
    //////////////////                GET                     //////////////////
    ////////////////////////////////////////////////////////////////////////////

    getCountries(): Observable<Countries[]> {
      return this.httpClient.get<Countries[]>(this.API_BOUNDARY_MANAGEMENT_URL + "/farm/findAllCountries")
        .pipe(
          //catchError(this.errorHandler)
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.GET-COUNTRIES'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getIntersectType(intersectId: number, fieldId: number): Observable<any> {
      return this.httpClient.get<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/field/getIntersectType?intersectId=" + intersectId + "&fieldId=" + fieldId)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.INTERSECT-TYPE'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getSubscribedFields(fieldIds: number[]): Observable<any> {
      return this.httpClient.get<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/field/getSubscribedFields?fieldIds=" + fieldIds)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.SUBSCRIPTIONS'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    ////////////////////////////////////////////////////////////////////////////
    ////////////////////             INSERT                  ///////////////////
    ////////////////////////////////////////////////////////////////////////////

    insertFarm(addFarm: AddFarm): Observable<any> {
      return this.httpClient.post<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/farm/insertFarm", addFarm)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ADD-FARM'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    insertFieldName(addField: AddField): Observable<any> {
      return this.httpClient.post<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/field/insertFieldName", addField)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ADD-FIELD'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    ////////////////////////////////////////////////////////////////////////////
    ////////////////////             UPDATE                  ///////////////////
    ////////////////////////////////////////////////////////////////////////////

    updateFarm(updateFarmDto: UpdateFarmDto): Observable<any> {
      return this.httpClient.put<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/farm/" + updateFarmDto.farmId, updateFarmDto)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    updateFieldMultiPolygonSupport(polygon: Polygon): Observable<any> {
        return this.httpClient.put<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/field/insertFieldMultiPolygon", polygon)
            .pipe(
                catchError(err => {
                    this.hasError.next(true);
                    this._snackBar.open(this.translate.instant('DATA-SERVICE.UPDATE-FIELD'), this.translate.instant('SNACKBAR.DISMISS'), {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }

    updateField(polygon: Polygon): Observable<any> {
      return this.httpClient.put<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/field/insertField", polygon)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.UPDATE-FIELD'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    removeArea(removeArea: RemoveArea): Observable<any> {
      return this.httpClient.put<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/field/removeAreas", removeArea)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.REMOVE-AREA'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    updateFieldContainingArea(fieldArea: FieldArea): Observable<any> {
      return this.httpClient.put<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/field/updateFieldContainingArea", fieldArea)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.UPDATE-AREA'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    restoreContainingFields(contaningField: ContaningField): Observable<any> {
      return this.httpClient.put<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/field/restoreContainingFields", contaningField)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.RESTORE-FIELD'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    updateFieldContainingFields(multiPolygon: MultiPolygonField): Observable<any> {
      return this.httpClient.put<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/field/updateFieldContainingFields", multiPolygon)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.UPDATE-FIELD'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    updateFieldName(fieldName: FieldName): Observable<any> {
      return this.httpClient.put<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/field/updateFieldName", fieldName)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.UPDATE-FIELD-NAME'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    updateCircularField(polygon: CirclePolygon): Observable<any> {
      return this.httpClient.put<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/field/insertCircularField", polygon)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.UPDATE-FIELD'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    ////////////////////////////////////////////////////////////////////////////
    ////////////////////             DELETE                  ///////////////////
    ////////////////////////////////////////////////////////////////////////////

    deleteFarm(farmId: FarmId): Observable<any> {
      return this.httpClient.put<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/farm/deleteFarm", farmId)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.DELETE-FARM'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    deleteField(fieldId: FieldId): Observable<any> {
      return this.httpClient.put<any>(this.API_BOUNDARY_MANAGEMENT_URL + "/field/deleteField", fieldId)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.DELETE-FIELD'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }
    
  }  
